import React, { createContext, useContext, useState, useCallback } from 'react'
import { Snackbar, Alert, AlertTitle, Box } from '@mui/material'

const ToastContext = createContext()

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([])

  const showToast = useCallback((message, severity = 'info', title = null, duration = 6000) => {
    const id = Date.now() + Math.random()
    const newToast = {
      id,
      message,
      severity,
      title,
      duration,
    }
    setToasts((prev) => [...prev, newToast])
    return id
  }, [])

  const showSuccess = useCallback(
    (message, title = null, duration = 6000) => {
      return showToast(message, 'success', title, duration)
    },
    [showToast]
  )

  const showError = useCallback(
    (message, title = null, duration = 6000) => {
      return showToast(message, 'error', title, duration)
    },
    [showToast]
  )

  const showWarning = useCallback(
    (message, title = null, duration = 6000) => {
      return showToast(message, 'warning', title, duration)
    },
    [showToast]
  )

  const showInfo = useCallback(
    (message, title = null, duration = 6000) => {
      return showToast(message, 'info', title, duration)
    },
    [showToast]
  )

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  const handleClose = useCallback(
    (id, event, reason) => {
      if (reason === 'clickaway') {
        return
      }
      removeToast(id)
    },
    [removeToast]
  )

  return (
    <ToastContext.Provider
      value={{
        showToast,
        showSuccess,
        showError,
        showWarning,
        showInfo,
      }}>
      {children}
      {/* Toast Container - Top Right */}
      <Box
        sx={{
          position: 'fixed',
          top: { xs: 16, sm: 20, md: 24 },
          right: { xs: 16, sm: 20, md: 24 },
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 1, sm: 1.25, md: 1.5 },
          maxWidth: { xs: 'calc(100% - 32px)', sm: 400, md: 450 },
          pointerEvents: 'none',
        }}>
        {toasts.map((toast, index) => (
          <Snackbar
            key={toast.id}
            open={true}
            autoHideDuration={toast.duration}
            onClose={(event, reason) => handleClose(toast.id, event, reason)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{
              position: 'relative',
              top: 0,
              right: 0,
              transform: 'none',
              pointerEvents: 'auto',
              mb: { xs: 1, sm: 1.25, md: 1.5 },
            }}>
            <Alert
              onClose={(event) => handleClose(toast.id, event, 'close')}
              severity={toast.severity}
              variant="outlined"
              sx={{
                width: '100%',
                fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light' ? '#ffffff' : 'rgba(255, 255, 255, 0.95)',
                color: (theme) =>
                  theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(0, 0, 0, 0.87)',
                border: (theme) => {
                  const colors = {
                    success:
                      theme.palette.mode === 'light'
                        ? 'rgba(76, 175, 80, 0.5)'
                        : 'rgba(129, 199, 132, 0.5)',
                    error:
                      theme.palette.mode === 'light'
                        ? 'rgba(244, 67, 54, 0.5)'
                        : 'rgba(239, 83, 80, 0.5)',
                    warning:
                      theme.palette.mode === 'light'
                        ? 'rgba(255, 152, 0, 0.5)'
                        : 'rgba(255, 183, 77, 0.5)',
                    info:
                      theme.palette.mode === 'light'
                        ? 'rgba(25, 118, 210, 0.5)'
                        : 'rgba(144, 202, 249, 0.5)',
                  }
                  return `1.5px solid ${colors[toast.severity] || colors.info}`
                },
                boxShadow: (theme) =>
                  theme.palette.mode === 'light'
                    ? '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)'
                    : '0 2px 8px rgba(0, 0, 0, 0.2), 0 1px 3px rgba(0, 0, 0, 0.15)',
                '& .MuiAlert-icon': {
                  fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' },
                  color: (theme) => {
                    const colors = {
                      success: theme.palette.mode === 'light' ? '#4caf50' : '#81c784',
                      error: theme.palette.mode === 'light' ? '#f44336' : '#ef5350',
                      warning: theme.palette.mode === 'light' ? '#ff9800' : '#ffb74d',
                      info: theme.palette.mode === 'light' ? '#1976d2' : '#90caf9',
                    }
                    return colors[toast.severity] || colors.info
                  },
                },
                '& .MuiAlert-message': {
                  color: (theme) =>
                    theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(0, 0, 0, 0.87)',
                },
                '& .MuiAlert-action': {
                  paddingTop: 0,
                  alignItems: 'flex-start',
                  '& .MuiIconButton-root': {
                    color: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.54)'
                        : 'rgba(0, 0, 0, 0.54)',
                    '&:hover': {
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'rgba(0, 0, 0, 0.04)'
                          : 'rgba(0, 0, 0, 0.08)',
                    },
                  },
                },
              }}>
              {toast.title && (
                <AlertTitle
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                    fontWeight: 600,
                    mb: toast.message ? { xs: 0.5, sm: 0.75, md: 1 } : 0,
                    color: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.87)'
                        : 'rgba(0, 0, 0, 0.87)',
                  }}>
                  {toast.title}
                </AlertTitle>
              )}
              {toast.message}
            </Alert>
          </Snackbar>
        ))}
      </Box>
    </ToastContext.Provider>
  )
}
